'use client';

import { AdSlot, type AdSlotProps } from '@/app_components/AdSlot/Adslot';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { AnalyticsEvent, ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

interface InstreamAdProps extends AdSlotProps {
  className?: string;
  dataCy?: string;
}

/**
 * Renders an in-stream advertisement slot within the content.
 *
 * @param {AdSlotProps} props - The properties for the ad slot.
 * @param {number} [props.index=0] - The index of the ad slot (0-indexed).
 * @param {string} props.placementName - The name of the ad placement.
 * @returns The rendered ad slot component, or null if ads should be hidden or configuration is missing.
 */
export const InstreamAd = ({
  index = 0,
  placementName,
  className = '',
  dataCy = ContentPlacement.InStream,
}: InstreamAdProps) => {
  const { pageAdConfig, hideInStreamAds } = useAdContext();
  const {
    paywallState: { paywallCheck },
  } = usePaywall();

  if (!pageAdConfig || hideInStreamAds || paywallCheck === 'paywalled') {
    return null;
  }

  return (
    <div className='flex justify-center items-center'>
      <div
        className={cn('absolute h-[1px] w-[1px]', AnalyticsEvent.ImpressionElement)}
        data-cy={`${dataCy}-${index}`}
      />
      <div className={cn('text-center min-h-[50px]', className)}>
        <AdSlot
          index={index}
          placementName={placementName}
        />
      </div>
    </div>
  );
};
